import { useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MessageSquarePlus, Bug, Lightbulb, MessageCircle } from "lucide-react";

const KIND_META = {
  bug: { icon: Bug, label: "Report a bug", placeholder: "What went wrong? Steps to reproduce, expected vs actual…" },
  feature: { icon: Lightbulb, label: "Request a feature", placeholder: "What would you like LeadForge to do?" },
  general: { icon: MessageCircle, label: "General feedback", placeholder: "Share your thoughts, praise, complaints…" },
};

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) { toast.error("Please add a bit more detail"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", { kind, message: message.trim(), page: loc.pathname });
      toast.success("Thanks! Your feedback was sent to the LeadForge team.");
      setMessage("");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={()=>setOpen(true)}
        className="fixed bottom-20 lg:bottom-5 right-4 lg:right-5 z-40 flex items-center gap-2 bg-zinc-950 text-white p-3 lg:pl-3 lg:pr-4 lg:py-2.5 rounded-full shadow-lg hover:bg-[#FF5A00] transition-colors font-medium text-sm"
        data-testid="feedback-widget-btn"
        aria-label="Send feedback"
      >
        <MessageSquarePlus className="w-4 h-4"/>
        <span className="hidden lg:inline">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>Report bugs, request features, or share general thoughts. Goes straight to the LeadForge team.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <Tabs value={kind} onValueChange={setKind}>
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="bug" data-testid="feedback-tab-bug"><Bug className="w-3.5 h-3.5 mr-1"/>Bug</TabsTrigger>
                <TabsTrigger value="feature" data-testid="feedback-tab-feature"><Lightbulb className="w-3.5 h-3.5 mr-1"/>Feature</TabsTrigger>
                <TabsTrigger value="general" data-testid="feedback-tab-general"><MessageCircle className="w-3.5 h-3.5 mr-1"/>General</TabsTrigger>
              </TabsList>
            </Tabs>
            <div>
              <Textarea
                required
                minLength={3}
                maxLength={5000}
                rows={6}
                placeholder={KIND_META[kind].placeholder}
                value={message}
                onChange={(e)=>setMessage(e.target.value)}
                data-testid="feedback-message-input"
              />
              <div className="text-[11px] text-zinc-400 mt-1 font-mono-accent">{message.length}/5000 · will include page: {loc.pathname}</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-[#FF5A00] hover:bg-[#E04F00] text-white" data-testid="feedback-submit-btn">
                {busy ? "Sending…" : "Send feedback"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
